import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CheckCircle, AlertCircle } from 'lucide-react';
import { usePayment } from '../contexts/PaymentContext';

const PaymentSuccess = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { checkPaymentStatus } = usePayment();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [verificationAttempted, setVerificationAttempted] = useState(false);

  useEffect(() => {
    // Only run verification once
    if (verificationAttempted) {
      return;
    }

    const verifyPayment = async () => {
      try {
        setVerificationAttempted(true);

        // Extract payment or subscription ID from URL query parameters
        const queryParams = new URLSearchParams(location.search);
        const paymentId = queryParams.get('payment_id');
        const subscriptionId = queryParams.get('subscription_id');
        const status = queryParams.get('status');
        const transactionId = paymentId || subscriptionId;

        // If URL contains status=active, we can trust it's successful
        if (status === 'active') {
          localStorage.setItem('hasCompletedPayment', 'true');
          setLoading(false);

          // Show success page for 3 seconds, then redirect
          setTimeout(() => {
            navigate('/onboarding/business');
          }, 3000);
          return;
        }

        // First check if the URL contains a failed status
        if (status === 'failed' || status === 'canceled' || status === 'cancelled') {
          setError(`Payment ${status}. Please try again or contact support if you believe this is an error.`);
          setLoading(false);
          return;
        }

        if (transactionId) {
          // Attempt to verify the payment/subscription with Dodo
          try {
            const paymentData = await checkPaymentStatus(transactionId);

            // Consider both actual successful statuses and our manual verification as success
            if (paymentData && (
              paymentData.status === 'paid' ||
              paymentData.status === 'completed' ||
              paymentData.status === 'active' ||
              paymentData.status === 'trialing' ||
              paymentData.status === 'manually_verified'
            )) {
              // Success! Store completion status
              localStorage.setItem('hasCompletedPayment', 'true');

              // Clean up pending data
              localStorage.removeItem('pendingPaymentId');
              localStorage.removeItem('pendingSubscriptionId');
              localStorage.removeItem('pendingPaymentData');
              localStorage.removeItem('pendingSubscriptionData');

              // Show success page for 3 seconds, then redirect
              setTimeout(() => setLoading(false), 1000);
              setTimeout(() => {
                navigate('/onboarding/business');
              }, 3000);
            } else if (status === 'active') {
              // If URL says active but verification failed, trust the URL
              localStorage.setItem('hasCompletedPayment', 'true');
              setLoading(false);

              // Show success page for 3 seconds, then redirect
              setTimeout(() => {
                navigate('/onboarding/business');
              }, 3000);
            } else {
              // Payment not confirmed as successful
              setError(`Payment verification failed. Status: ${paymentData.status || 'unknown'}. Please contact support if your payment was processed.`);
              setLoading(false);
            }
          } catch (error) {
            // If URL says active but verification throws error, trust the URL
            if (status === 'active') {
              localStorage.setItem('hasCompletedPayment', 'true');
              setLoading(false);

              // Show success page for 3 seconds, then redirect
              setTimeout(() => {
                navigate('/onboarding/business');
              }, 3000);
              return;
            }

            // Don't assume success if verification fails
            setError('Unable to verify payment status. Please contact support if your payment was processed.');
            setLoading(false);
          }
        } else {
          // No transaction ID in URL
          const pendingPaymentId = localStorage.getItem('pendingPaymentId');
          const pendingSubscriptionId = localStorage.getItem('pendingSubscriptionId');
          const pendingTransactionId = pendingPaymentId || pendingSubscriptionId;

          if (pendingTransactionId) {
            // We have a pending transaction ID, try to verify it
            try {
              const paymentData = await checkPaymentStatus(pendingTransactionId);

              if (paymentData && (
                paymentData.status === 'paid' ||
                paymentData.status === 'completed' ||
                paymentData.status === 'active' ||
                paymentData.status === 'trialing' ||
                paymentData.status === 'manually_verified'
              )) {
                localStorage.setItem('hasCompletedPayment', 'true');
                setLoading(false);

                // Show success page for 3 seconds, then redirect
                setTimeout(() => {
                  navigate('/onboarding/business');
                }, 3000);
              } else {
                setError(`Payment verification failed. Status: ${paymentData.status || 'unknown'}. Please try again.`);
                setLoading(false);
              }
            } catch (error) {
              setError('Unable to verify payment status. Please try again or contact support.');
              setLoading(false);
            }

            localStorage.removeItem('pendingPaymentId');
            localStorage.removeItem('pendingSubscriptionId');
            localStorage.removeItem('pendingPaymentData');
            localStorage.removeItem('pendingSubscriptionData');
          } else {
            // No transaction ID anywhere
            setError('No payment information found. Please try again.');
            setLoading(false);
          }
        }
      } catch (error) {
        setError('An unexpected error occurred. Please try again or contact support.');
        setLoading(false);
      }
    };

    verifyPayment();
  }, [location.search, checkPaymentStatus, navigate, verificationAttempted]);

  if (loading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-primary-500 border-t-transparent mx-auto"></div>
        <p className="mt-4 text-gray-600">Verifying your payment...</p>
      </div>
    );
  }

  if (error) {
    // Check URL status one last time before showing error
    const queryParams = new URLSearchParams(location.search);
    const status = queryParams.get('status');

    // If URL indicates success, override the error
    if (status === 'active') {
      return (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 md:p-12">
            <div className="flex justify-center mb-6">
              <CheckCircle size={80} className="text-green-500" />
            </div>

            <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>

            <p className="text-gray-600 mb-8 text-lg">
              Thank you for your purchase. Your HyperRank subscription is now active.
            </p>

            <p className="text-gray-600 mb-4">
              You will be redirected to the onboarding process in a few seconds...
            </p>

            <div className="flex justify-center space-x-4">
              <Link
                to="/onboarding/business"
                className="px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
              >
                Continue to Onboarding
              </Link>

              <Link
                to="/dashboard"
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors"
              >
                Go to Dashboard
              </Link>
            </div>
          </div>
        </div>
      );
    }

    return (
      <div className="max-w-3xl mx-auto px-4 py-16 text-center">
        <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 md:p-12">
          <div className="flex justify-center mb-6">
            <AlertCircle size={80} className="text-yellow-500" />
          </div>

          <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Verification Issue</h1>

          <p className="text-gray-600 mb-8 text-lg">
            {error}
          </p>

          <div className="flex justify-center space-x-4">
            <Link
              to="/checkout"
              className="px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
            >
              Return to Checkout
            </Link>

            <Link
              to="/support"
              className="px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors"
            >
              Contact Support
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 py-16 text-center">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8 md:p-12">
        <div className="flex justify-center mb-6">
          <CheckCircle size={80} className="text-green-500" />
        </div>

        <h1 className="text-3xl font-bold text-gray-900 mb-4">Payment Successful!</h1>

        <p className="text-gray-600 mb-8 text-lg">
          Thank you for your purchase. Your HyperRank subscription is now active.
        </p>

        <p className="text-gray-600 mb-4">
          You will be redirected to the onboarding process in a few seconds...
        </p>

        <div className="flex justify-center space-x-4">
          <Link
            to="/onboarding/business"
            className="px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
          >
            Continue to Onboarding
          </Link>

          <Link
            to="/dashboard"
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-full font-medium hover:bg-gray-50 transition-colors"
          >
            Go to Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess; 